import { useEffect, useRef } from 'react';
import Konva from 'konva';
import { KonvaCanvas } from '../../core';
import {
  SelectionManager,
  DragManager,
  CreationManager,
  ConnectorManager,
  HistoryManager,
  ClipboardManager,
  GroupingManager,
  LayerOrderManager,
  AnchorManager,
} from '../../managers';
import { EventEmitter } from '../../core/EventEmitter';
import { getEdgeAnchorFromOffsetAnchor, isShapeElement } from '../../utils/connectorUtils';
import { syncConnectorsWithElements } from '../../utils/connectorSync';
import { AddElementCommand } from '../../editing/UndoRedoManager';
import { ConnectorElement } from '../../editing/types';
import { createImage } from '../../editing/ElementFactory';
import { duplicateElementFromAnchor } from '../../utils/duplicationUtils';
import { UseCanvasStateReturn } from './useCanvasState';
import { BoardCanvasInnerProps, CanvasApi } from '../../BoardCanvas.types';

export function useCanvasInitialization(
  props: BoardCanvasInnerProps,
  state: UseCanvasStateReturn
) {
  const {
    width = 1200,
    height = 800,
    backgroundColor = '#F5F5F5',
    enableRotation = true,
    enableResize = true,
    keepAspectRatio = false,
    enableAlignment = true,
    initialElements = [],
    mode: initialMode = 'view',
    onElementsChange,
    onCanvasReady,
  } = props;

  const {
    containerRef,
    canvasRef,
    emitterRef,
    selectionManagerRef,
    dragManagerRef,
    creationManagerRef,
    connectorManagerRef,
    historyManagerRef,
    clipboardManagerRef,
    groupingManagerRef,
    layerOrderManagerRef,
    anchorManagerRef,
    elementsRef,
    modeRef,
    createModeRef,
    connectorModeRef,
    clearSelectionRef,
    setElements,
    setZoomControls,
    setStageTransform,
    setIsTransforming,
    setContextMenu,
    selectElement,
    selectElements,
  } = state;

  // Track if a marquee selection just completed to prevent click handler from clearing it
  const justCompletedMarqueeRef = useRef(false);

  useEffect(() => {
    if (!containerRef.current) return;
    
    const emitter = emitterRef.current;
    const container = containerRef.current;
    
    // Make container focusable for paste events
    container.setAttribute('tabindex', '0');
    container.style.outline = 'none';
    
    // Create canvas with shared emitter
    const canvas = new KonvaCanvas(container, {
      width,
      height,
      backgroundColor,
      emitter, // Use the same emitter for both canvas and managers
    });
    canvasRef.current = canvas;
    
    const layer = canvas.getLayer();
    
    // Note: Context menu copy tracking is now handled by ClipboardManager
    
    // Create managers
    selectionManagerRef.current = new SelectionManager(layer, emitter, {
      enableRotation,
      enableResize,
      keepAspectRatio,
    });
    
    dragManagerRef.current = new DragManager(layer, emitter, {
      enableSnapping: enableAlignment,
    });
    
    creationManagerRef.current = new CreationManager(layer, emitter);
    connectorManagerRef.current = new ConnectorManager(layer, emitter);
    historyManagerRef.current = new HistoryManager(emitter);
    // Initialize history manager with setElements immediately so commands work
    historyManagerRef.current.setElementsRef(initialElements, setElements);
    clipboardManagerRef.current = new ClipboardManager(emitter);
    groupingManagerRef.current = new GroupingManager(emitter);
    layerOrderManagerRef.current = new LayerOrderManager(emitter);
    anchorManagerRef.current = new AnchorManager(layer);
    anchorManagerRef.current.setElements(initialElements);
    
    // Setup anchor callbacks - different behavior for shapes vs non-shapes
    anchorManagerRef.current.setOnAnchorClick((anchor, element) => {
      if (isShapeElement(element)) {
        // For shapes: create connector + duplicate shape (existing behavior)
        const edgeAnchor = getEdgeAnchorFromOffsetAnchor(anchor, element, 18);
        connectorManagerRef.current?.handleAnchorClick(edgeAnchor, element, 18);
      } else {
        // For non-shapes: duplicate element only (no connector)
        const newElement = duplicateElementFromAnchor(element, anchor, 50);
        // Add element to canvas using history manager
        if (historyManagerRef.current) {
          historyManagerRef.current.addElement(newElement);
        }
        // Select the new element
        selectElement(newElement.id, false);
      }
    });
    
    anchorManagerRef.current.setOnAnchorDrag(
      (anchor, element) => {
        // Drag start: convert offset anchor to edge and start connector creation
        const edgeAnchor = getEdgeAnchorFromOffsetAnchor(anchor, element, 18);
        connectorManagerRef.current?.handleAnchorDragStart(edgeAnchor, element, 18);
      },
      (anchor, element, pointerPos) => {
        // Drag move: update connector preview
        connectorManagerRef.current?.updatePreview(pointerPos, false);
      },
      (anchor, element, pointerPos) => {
        // Drag end: finish connector creation
        const edgeAnchor = getEdgeAnchorFromOffsetAnchor(anchor, element, 18);
        connectorManagerRef.current?.handleAnchorDragEnd(edgeAnchor, element, pointerPos, 18);
      }
    );
    
    // Setup event listeners on canvas
    canvas.on('ready', ({ fitToContent, zoomControls, transform }) => {
      setZoomControls(zoomControls);
      setStageTransform(transform);
    });
    
    canvas.on('transformChange', ({ transform }) => {
      setStageTransform(transform);
    });
    
    // Listen to selection changes from SelectionManager via emitter
    if (emitter) {
      emitter.on('selectionChange', ({ selectedIds: ids }) => {
        selectElements?.(ids, true);
      });
    }
    
    // Setup selection manager callbacks
    selectionManagerRef.current.setOnTransformStart(() => {
      setIsTransforming(true);
      // Hide anchors during transform
      anchorManagerRef.current?.setTransforming(true);
    });
    
    selectionManagerRef.current.setOnTransformEnd((id, newAttrs, oldAttrs) => {
      setIsTransforming(false);
      // Update the renderer directly (avoid full re-render)
      const renderer = canvasRef.current?.getRenderer(id);
      if (renderer) {
        renderer.update(newAttrs);
      }

      // Update elements ref
      if (elementsRef.current) {
        elementsRef.current = elementsRef.current.map((el) =>
          el.id === id ? { ...el, ...newAttrs } : el
        );
      }

      // Update all managers with new elements
      dragManagerRef.current?.setElements(elementsRef.current);
      connectorManagerRef.current?.setElements(elementsRef.current);
      historyManagerRef.current?.updateElementsRef(elementsRef.current);
      anchorManagerRef.current?.setElements(elementsRef.current);
      // Re-enable anchors after transform ends
      anchorManagerRef.current?.setTransforming(false);

      // Sync connectors when element is transformed
      if (elementsRef.current) {
        const connectors = elementsRef.current.filter((el) => el.type === 'connector') as ConnectorElement[];
        if (connectors.length > 0 && canvasRef.current) {
          const syncedConnectors = syncConnectorsWithElements(connectors, elementsRef.current, id);
          // Update connector renderers with synced data and position
          syncedConnectors.forEach((connector) => {
            const connectorRenderer = canvasRef.current?.getRenderer(connector.id);
            if (connectorRenderer) {
              // Update both position and data to ensure connector is positioned correctly
              connectorRenderer.update({ 
                x: connector.x, 
                y: connector.y, 
                width: connector.width, 
                height: connector.height,
                data: connector.data 
              });
            }
            // Also update the element in elementsRef to keep it in sync
            if (elementsRef.current) {
              const index = elementsRef.current.findIndex(el => el.id === connector.id);
              if (index !== -1) {
                elementsRef.current[index] = connector;
              }
            }
          });
        }
      }

      // Record transform for undo (without triggering re-render)
      if (oldAttrs) {
        historyManagerRef.current?.recordPositionChange(id, oldAttrs, newAttrs);
      }

      // Notify parent
      onElementsChange?.(elementsRef.current);
    });
    
    // Setup creation manager callback
    creationManagerRef.current.setOnElementCreated((element) => {
      // historyManager.addElement already calls setElements internally
      historyManagerRef.current?.addElement(element);
    });
    
    // Setup clipboard callbacks
    clipboardManagerRef.current.setOnPaste((newElements) => {
      // historyManager.addElement already calls setElements internally
      newElements.forEach((el) => {
        historyManagerRef.current?.addElement(el);
      });
    });
    
    clipboardManagerRef.current.setOnCut((cutElements) => {
      // historyManager.deleteElements already calls setElements internally
      historyManagerRef.current?.deleteElements(cutElements.map((el) => el.id));
    });
    
    // Setup grouping callback
    groupingManagerRef.current.setOnGroupChange((newElements, groupId, ungroupedIds) => {
      setElements(newElements);
      if (groupId) {
        selectElement(groupId, false);
      } else if (ungroupedIds) {
        selectElements?.(ungroupedIds, true);
      }
    });
    
    // Setup layer order callback
    layerOrderManagerRef.current.setOnOrderChange((newElements) => {
      setElements(newElements);
    });
    
    // Setup connector manager callback
    connectorManagerRef.current.setOnConnectorCreated((connector, newShape) => {
      // Use batch command to add both elements atomically to avoid race conditions
      if (newShape && historyManagerRef.current && elementsRef.current) {
        const currentElements = elementsRef.current;
        // Create commands that will execute sequentially, with the second using updated elements
        const commands = [
          new AddElementCommand(currentElements, newShape, setElements),
          new AddElementCommand([...currentElements, newShape], connector, setElements),
        ];
        historyManagerRef.current.batch(commands, 'Create connector with shape');
      
      } else {
        // Only connector, no new shape
        historyManagerRef.current?.addElement(connector);

      }
    });
    
    // Call onCanvasReady with the API
    if (onCanvasReady) {
      onCanvasReady({
        toDataURL: (options?: { pixelRatio?: number; mimeType?: string }) => canvas.toDataURL(options),
        exportImage: (filename?: string) => canvas.downloadPNG(filename),
        fitToContent: () => canvas.fitToContent(),
        zoomIn: () => canvas.zoomIn(),
        zoomOut: () => canvas.zoomOut(),
        getTransform: () => canvas.getTransform(),
      });
    }
    
    // Set initial panning state based on mode
    // In edit mode with select tool, disable panning so marquee selection works
    // In pan mode, panning is allowed
    if (initialMode === 'edit') {
      // Initial createMode is 'select', so disable panning
      canvas.setPanningDisabled(true);
    }

    // Setup stage event handlers for interactions
    const stage = canvas.getStage();
    
    // Show cursor preview when mouse enters stage in create mode
    stage.on('mouseenter', (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (modeRef.current !== 'edit') return;
      
      if (createModeRef.current.type !== 'select' && 
          createModeRef.current.type !== 'pan' && 
          !creationManagerRef.current?.isCreating()) {
        const pointerPos = stage.getPointerPosition();
        if (pointerPos) {
          const pos = canvas.screenToLayer(pointerPos);
          creationManagerRef.current?.showCursorPreview(pos.x, pos.y);
        }
      }
    });
    
    // Handle stage click for selection
    stage.on('click', (e: Konva.KonvaEventObject<MouseEvent>) => {
      // Skip clearing selection if a marquee just completed (to prevent clearing the selection made by marquee)
      if (justCompletedMarqueeRef.current) {
        justCompletedMarqueeRef.current = false;
        return;
      }
      
      // Check if clicked on stage (empty area)
      const clickedOnEmpty = e.target === stage || 
        e.target.getClassName() === 'Layer' ||
        (e.target.attrs && e.target.attrs.fillPatternRepeat === 'repeat');
      
      if (clickedOnEmpty) {
        // Clear selection when clicking on empty area
        if (!e.evt.shiftKey && !e.evt.ctrlKey && !e.evt.metaKey) {
          // Cancel any active marquee state first
          selectionManagerRef.current?.cancelMarquee();
          // Clear both Konva transformer and React selection state
          selectionManagerRef.current?.clearSelection();
          if (clearSelectionRef.current) {
            clearSelectionRef.current();
          }
        }
      }
    });
    
    // Marquee selection - mousedown on empty area starts marquee (only in select mode)
    stage.on('mousedown', (e: Konva.KonvaEventObject<MouseEvent>) => {
      // Only in edit mode
      if (modeRef.current !== 'edit') return;
      
      // Only handle left mouse button
      if (e.evt.button !== 0) return;
      
      // Check if connector mode is active
      if (connectorModeRef.current) {
        // Check if clicked on stage (empty area)
        const clickedOnEmpty = e.target === stage || 
          e.target.getClassName() === 'Layer' ||
          (e.target.attrs && e.target.attrs.fillPatternRepeat === 'repeat');
        
        if (clickedOnEmpty) {
          // Start connector from empty canvas
          const pointerPos = stage.getPointerPosition();
          if (pointerPos) {
            const pos = canvas.screenToLayer(pointerPos);
            const isPrecise = e.evt.ctrlKey || e.evt.metaKey;
            connectorManagerRef.current?.startFromEmpty(pos);
          }
        }
        // If clicked on a shape, the element click handler will handle it
        return;
      }
      
      // Handle creation mode - start creation on mousedown (for drag) or prepare for click
      if (createModeRef.current.type !== 'select' && createModeRef.current.type !== 'pan') {
        // Check if clicked on stage (empty area)
        const clickedOnEmpty = e.target === stage || 
          e.target.getClassName() === 'Layer' ||
          (e.target.attrs && e.target.attrs.fillPatternRepeat === 'repeat');
        
        if (clickedOnEmpty) {
          const pointerPos = stage.getPointerPosition();
          if (pointerPos) {
            const pos = canvas.screenToLayer(pointerPos);
            // Start creation (will create on mouseup if it's a click, or allow drag)
            creationManagerRef.current?.startCreating(pos.x, pos.y);
          }
        }
        return;
      }
      
      // Only handle marquee in select mode
      if (createModeRef.current.type !== 'select') return;
      
      // Check if clicked on stage (empty area)
      const clickedOnEmpty = e.target === stage || 
        e.target.getClassName() === 'Layer' ||
        (e.target.attrs && e.target.attrs.fillPatternRepeat === 'repeat');
      
      if (clickedOnEmpty) {
        // Get pointer position in layer coordinates
        const pos = canvas.screenToLayer(stage.getPointerPosition()!);
        selectionManagerRef.current?.startMarquee(pos.x, pos.y);
      }
    });
    
    // Marquee selection - mousemove updates marquee
    stage.on('mousemove', (e: Konva.KonvaEventObject<MouseEvent>) => {
      if (modeRef.current !== 'edit') return;
      
      // Handle connector preview during creation
      if (connectorModeRef.current && connectorManagerRef.current?.isActive()) {
        const pointerPos = stage.getPointerPosition();
        if (pointerPos) {
          const pos = canvas.screenToLayer(pointerPos);
          const isPrecise = e.evt.ctrlKey || e.evt.metaKey;
          connectorManagerRef.current.updatePreview(pos, isPrecise);
        }
        return;
      }
      
      // Handle creation mode - update creation preview during drag
      if (createModeRef.current.type !== 'select' && 
          createModeRef.current.type !== 'pan' && 
          creationManagerRef.current?.isCreating()) {
        const pointerPos = stage.getPointerPosition();
        if (pointerPos) {
          const pos = canvas.screenToLayer(pointerPos);
          creationManagerRef.current?.updateCreating(pos.x, pos.y);
        }
        return;
      }
      
      // Handle cursor preview for creation modes (when not creating/dragging)
      if (createModeRef.current.type !== 'select' && 
          createModeRef.current.type !== 'pan' && 
          !creationManagerRef.current?.isCreating()) {
        const pointerPos = stage.getPointerPosition();
        if (pointerPos) {
          const pos = canvas.screenToLayer(pointerPos);
          // Update or show cursor preview
          creationManagerRef.current?.updateCursorPreviewPosition(pos.x, pos.y);
        }
        return;
      }
      
      // Only handle marquee in select mode
      if (createModeRef.current.type !== 'select') return;
      if (!selectionManagerRef.current?.isMarqueeActive()) return;
      
      const pos = canvas.screenToLayer(stage.getPointerPosition()!);
      selectionManagerRef.current?.updateMarquee(pos.x, pos.y);
    });
    
    // Marquee selection - mouseup ends marquee
    stage.on('mouseup', (e: Konva.KonvaEventObject<MouseEvent>) => {
      // Handle connector creation finish
      if (connectorModeRef.current && connectorManagerRef.current?.isActive()) {
        connectorManagerRef.current.finish();
        return;
      }
      
      // Handle creation mode - finish creating element
      if (createModeRef.current.type !== 'select' && 
          createModeRef.current.type !== 'pan' && 
          creationManagerRef.current?.isCreating()) {
        const newElement = creationManagerRef.current?.finishCreating();
        if (newElement) {
          // Auto-switch to select mode (already handled in finishCreating, but update state)
          state.setCreateMode({ type: 'select' });
          creationManagerRef.current?.setMode({ type: 'select' });
        }
        return;
      }
      
      // Always end marquee if active (even if mode changed)
      if (!selectionManagerRef.current?.isMarqueeActive()) return;
      
      // Only select elements if in edit mode with select tool
      if (modeRef.current === 'edit' && createModeRef.current && createModeRef.current.type === 'select') {
        const addToSelection = e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey;
        const selectedIds = selectionManagerRef.current?.endMarquee(addToSelection) || [];
        // Mark that marquee just completed to prevent click handler from clearing selection
        // Only set flag if elements were actually selected (prevents blocking clear when marquee selected nothing)
        if (selectedIds.length > 0) {
          justCompletedMarqueeRef.current = true;
        }
      } else {
        // Just cancel the marquee without selecting
        selectionManagerRef.current?.cancelMarquee();
      }
    });
    
    // Handle context menu (show on right-click, both on elements and free space)
    stage.on('contextmenu', (e: Konva.KonvaEventObject<PointerEvent>) => {
      e.evt.preventDefault();
      const selectedIds = selectionManagerRef.current?.getSelectedIds() || [];
      
      // Show context menu even if no elements are selected (right-click on free space)
      setContextMenu({
        x: e.evt.clientX,
        y: e.evt.clientY,
        selectedIds: [...selectedIds],
      });
    });
    
    // ============================================================================
    // DRAG AND DROP / PASTE HANDLERS FOR IMAGES
    // ============================================================================
    
    /**
     * Convert file to base64 data URL
     */
    const fileToDataURL = (file: File): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target?.result && typeof e.target.result === 'string') {
            resolve(e.target.result);
          } else {
            reject(new Error('Failed to read file'));
          }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      });
    };
    
    /**
     * Create image element from file at position
     */
    const createImageFromFile = async (
      file: File,
      x: number,
      y: number
    ): Promise<void> => {
      // Check if file is an image
      if (!file.type.startsWith('image/')) {
        console.warn('Dropped file is not an image:', file.type);
        return;
      }
      
      try {
        // Convert to base64
        const dataURL = await fileToDataURL(file);
        
        // Create image element
        const imageElement = createImage({
          x,
          y,
          data: {
            src: dataURL,
            alt: file.name,
            preserveAspectRatio: true,
          },
        });
        
        // Add to canvas via history manager
        historyManagerRef.current?.addElement(imageElement);
      } catch (error) {
        console.error('Failed to create image from file:', error);
      }
    };
    
    // Drag and drop handlers
    let dragDepth = 0;
    let isDraggingOver = false;
    
    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragDepth++;
      if (dragDepth === 1) {
        isDraggingOver = true;
        // Visual feedback could be added here
      }
    };
    
    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragDepth--;
      if (dragDepth === 0) {
        isDraggingOver = false;
      }
    };
    
    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    
    const handleDrop = async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragDepth = 0;
      isDraggingOver = false;
      
      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length === 0) return;
      
      if (!container) return;
      
      // Get drop position in layer coordinates
      const rect = container.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;
      const layerPos = canvas.screenToLayer({ x: screenX, y: screenY });
      
      // Create image elements for each dropped file
      for (const file of files) {
        await createImageFromFile(file, layerPos.x, layerPos.y);
        // Offset subsequent images slightly
        layerPos.x += 20;
        layerPos.y += 20;
      }
    };
    
    // Guard to prevent duplicate paste handling
    let isHandlingPaste = false;
    
    // Paste handler
    const handlePaste = async (e: ClipboardEvent) => {
      // Prevent duplicate handling
      if (isHandlingPaste) {
        return;
      }
      
      isHandlingPaste = true;
      
      // Don't handle paste if user is typing in an input/textarea
      const target = e.target as HTMLElement;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target.isContentEditable && target.contentEditable === 'true')
      ) {
        isHandlingPaste = false;
        return;
      }
      
      // Check if paste is happening on or near the canvas
      // Allow paste if clicking on canvas container or stage
      const isCanvasTarget = container && (
        container.contains(target) ||
        target === container ||
        stage.container().contains(target) ||
        target === stage.container() ||
        container.contains(document.activeElement)
      );
      
      // If not on canvas and canvas is not focused, don't handle
      if (!isCanvasTarget) {
        isHandlingPaste = false;
        return;
      }
      
      // Priority logic:
      // 1. If copy was done via context menu, prioritize canvas clipboard
      // 2. Otherwise, prioritize images in system clipboard over stale canvas clipboard
      
      const items = e.clipboardData?.items;
      const imageFiles: File[] = [];
      
      if (items && items.length > 0) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.kind === 'file' && item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) {
              imageFiles.push(file);
            }
          }
        }
      }
      
      const hasCanvasClipboard = clipboardManagerRef.current?.hasClipboard();
      const wasCopiedViaContextMenu = clipboardManagerRef.current?.wasCopiedViaContextMenu() || false;
      
      // Priority 1: If copy was done via context menu, prioritize canvas clipboard
      if (wasCopiedViaContextMenu && hasCanvasClipboard && clipboardManagerRef.current) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Get paste position (use pointer position or center of viewport)
        const stage = canvasRef.current?.getStage();
        if (stage) {
          const pointerPos = stage.getPointerPosition();
          let layerPos: { x: number; y: number } | undefined;
          
          if (pointerPos) {
            layerPos = canvas.screenToLayer(pointerPos);
          } else {
            // Use center of viewport
            const transform = canvas.getTransform();
            const centerX = (width / 2 - transform.x) / transform.scale;
            const centerY = (height / 2 - transform.y) / transform.scale;
            layerPos = { x: centerX, y: centerY };
          }
          
          clipboardManagerRef.current.paste(layerPos);
        } else {
          clipboardManagerRef.current.paste();
        }
        
        clipboardManagerRef.current.resetContextMenuFlag(); // Reset flag after paste
        setTimeout(() => {
          isHandlingPaste = false;
        }, 100);
        return;
      }
      
      // Priority 2: If we have image files, prioritize them over canvas clipboard
      if (imageFiles.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation(); // Prevent other handlers from firing
        
        // Get paste position (use pointer position or center of viewport)
        const stage = canvasRef.current?.getStage();
        if (!stage) {
          isHandlingPaste = false;
          return;
        }
        
        const pointerPos = stage.getPointerPosition();
        let layerPos: { x: number; y: number };
        
        if (pointerPos) {
          layerPos = canvas.screenToLayer(pointerPos);
        } else {
          // Use center of viewport
          const transform = canvas.getTransform();
          const centerX = (width / 2 - transform.x) / transform.scale;
          const centerY = (height / 2 - transform.y) / transform.scale;
          layerPos = { x: centerX, y: centerY };
        }
        
        // Create image elements for each pasted file
        for (const file of imageFiles) {
          await createImageFromFile(file, layerPos.x, layerPos.y);
          // Offset subsequent images slightly
          layerPos.x += 20;
          layerPos.y += 20;
        }
        
        // Reset flag after paste completes
        isHandlingPaste = false;
        return;
      }
      
      // Priority 3: Check for canvas elements (internal clipboard)
      // Only if no images were found and copy wasn't done via context menu
      if (hasCanvasClipboard && clipboardManagerRef.current) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation(); // Prevent other handlers from firing
        
        // Get paste position (use pointer position or center of viewport)
        const stage = canvasRef.current?.getStage();
        if (stage) {
          const pointerPos = stage.getPointerPosition();
          let layerPos: { x: number; y: number } | undefined;
          
          if (pointerPos) {
            layerPos = canvas.screenToLayer(pointerPos);
          } else {
            // Use center of viewport
            const transform = canvas.getTransform();
            const centerX = (width / 2 - transform.x) / transform.scale;
            const centerY = (height / 2 - transform.y) / transform.scale;
            layerPos = { x: centerX, y: centerY };
          }
          
          clipboardManagerRef.current.paste(layerPos);
        } else {
          clipboardManagerRef.current.paste();
        }
        
        // Reset flag after a short delay to allow async operations to complete
        setTimeout(() => {
          isHandlingPaste = false;
        }, 100);
        return;
      }
      
      // No images and no canvas elements, let default behavior proceed
      isHandlingPaste = false;
      return;
    };
    
    // Add event listeners
    if (container) {
      container.addEventListener('dragenter', handleDragEnter);
      container.addEventListener('dragleave', handleDragLeave);
      container.addEventListener('dragover', handleDragOver);
      container.addEventListener('drop', handleDrop);
    }
    // Attach paste to document only (avoid duplicate handling)
    // Use capture phase to catch it early and prevent other handlers from interfering
    document.addEventListener('paste', handlePaste, true);
    
    return () => {
      // Cleanup drag/drop and paste handlers
      if (container) {
        container.removeEventListener('dragenter', handleDragEnter);
        container.removeEventListener('dragleave', handleDragLeave);
        container.removeEventListener('dragover', handleDragOver);
        container.removeEventListener('drop', handleDrop);
      }
      document.removeEventListener('paste', handlePaste, true);
      
      if (emitter) {
        emitter.removeAllListeners();
      }
      canvas.destroy();
      selectionManagerRef.current?.destroy();
      dragManagerRef.current?.destroy();
      creationManagerRef.current?.destroy();
      connectorManagerRef.current?.destroy();
      historyManagerRef.current?.destroy();
      clipboardManagerRef.current?.destroy();
      groupingManagerRef.current?.destroy();
      layerOrderManagerRef.current?.destroy();
      anchorManagerRef.current?.destroy();
    };
  }, []);
}
